<template>
    <div
        :class="[
            $style.wrapper,
            props.embedded ? $style.wrapper_embedded : $style.wrapper_full,
            platform === 'desktop' ? $style.desktop : '',
        ]"
    >
        <GameCard
            v-for="(game, index) in props.games"
            :key="index"
            :game="game"
            :targetLink="props.gameLinkTarget"
            type="rectangle"
            :data-interface-target="`game-number-${index + 1}`"
            responsive
            :lazy="!props.eagerThumbs || index >= props.eagerThumbs"
            :preload="props.preloadFirstCard && index === 0"
            withBadges
            :ctrContext="props.ctrContext"
            withScaleOnHover
            :autoplayVideoInViewport="index === 0"
        />
        <MoreGamesCard
            v-if="props.link?.name"
            :games="props.games.slice(props.games.length - 3)"
            :to="props.link"
            type="rectangle"
            :categoryName="props.categoryName"
        />
    </div>
</template>
<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { RouteLocationNamedRaw } from 'vue-router'

import GameCard from '@/components/GameCard/GameCard.vue'
import type { CtrContext } from '@/composables/use-ctr-context'
import { useAppStore } from '@/store/app-store'
import type { LinkTarget, ShortGameType } from '@/types'

import MoreGamesCard from './MoreGamesCard.vue'

const props = defineProps<{
    games: ShortGameType[] | undefined[]
    eagerThumbs?: number
    preloadFirstCard?: boolean
    gameLinkTarget: LinkTarget | undefined
    link?: RouteLocationNamedRaw
    embedded?: boolean
    categoryName?: string
    ctrContext?: CtrContext
}>()

const appStore = useAppStore()
const { platform } = storeToRefs(appStore)
</script>
<style module>
.desktop {
}

.wrapper {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px 8px;
}

.wrapper > :first-child {
    grid-column: span 2;
}

.wrapper_full > :nth-child(-n + 5) {
    display: flex;
}

.wrapper_embedded > :nth-child(-n + 5) {
    display: flex;
}
.wrapper_embedded > :nth-child(n + 5) {
    display: none;
}

.wrapper > :nth-child(n + 5) {
    display: none;
}

@media (width >= 500px) {
    .wrapper.desktop {
        grid-template-columns: repeat(3, 1fr);
    }

    .wrapper.desktop > :first-child {
        grid-column: span 1;
    }

    .wrapper_full.desktop > :nth-child(-n + 12) {
        display: flex;
    }

    .wrapper_full.desktop > :nth-child(n + 12) {
        display: none;
    }

    .wrapper_embedded.desktop > :nth-child(-n + 6) {
        display: flex;
    }

    .wrapper_embedded.desktop > :nth-child(n + 6) {
        display: none;
    }
}

@media (width >= 640px) {
    .wrapper.desktop {
        grid-template-columns: repeat(4, 1fr);
    }

    .wrapper_full.desktop > :nth-child(-n + 12) {
        display: flex;
    }

    .wrapper_full.desktop > :nth-child(n + 12) {
        display: none;
    }

    .wrapper_embedded.desktop > :nth-child(-n + 8) {
        display: flex;
    }

    .wrapper_embedded.desktop > :nth-child(n + 8) {
        display: none;
    }
}

@media (width >= 960px) {
    .wrapper.desktop {
        grid-template-columns: repeat(5, 1fr);
    }

    .wrapper_full.desktop > :nth-child(-n + 10) {
        display: flex;
    }

    .wrapper_full.desktop > :nth-child(n + 10) {
        display: none;
    }

    .wrapper_embedded.desktop > :nth-child(-n + 5) {
        display: flex;
    }

    .wrapper_embedded.desktop > :nth-child(n + 5) {
        display: none;
    }
}

@media (width >= 1300px) {
    .wrapper.desktop {
        grid-template-columns: repeat(6, 1fr);
    }

    .wrapper_full.desktop > :nth-child(-n + 12) {
        display: flex;
    }

    .wrapper_full.desktop > :nth-child(n + 12) {
        display: none;
    }

    .wrapper_embedded.desktop > :nth-child(-n + 6) {
        display: flex;
    }

    .wrapper_embedded.desktop > :nth-child(n + 6) {
        display: none;
    }
}

/* Ensure the last card (MoreGamesCard) is always visible */
.wrapper > :last-child {
    display: flex;
}
.wrapper.desktop > :last-child {
    display: flex;
}
</style>
